import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { resumeAPI } from '../lib/api';
import {
  FileText, Briefcase, GraduationCap, Award, FolderOpen, Loader2,
  Mail, Phone, MapPin, Globe, Linkedin, ExternalLink, User, Star, Download
} from 'lucide-react';

function ImpactView({ resume, photo, ac }) {
  const c = resume.contact || {};
  return (
    <div className="bg-white text-gray-900 overflow-hidden shadow-2xl" style={{ fontSize: 12 }}>
      <div className="px-8 py-5" style={{ background: ac, color: '#fff' }}>
        <div className="flex items-center gap-5">
          {photo && <img src={`data:image/jpeg;base64,${photo}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white/30" />}
          <div>
            <h1 className="text-2xl font-black uppercase tracking-wide">{c.name || 'Professional'}</h1>
            <p className="text-sm opacity-90 mt-0.5">{resume.experience?.[0]?.title || ''}</p>
          </div>
        </div>
      </div>
      {resume.summary && (
        <div className="px-8 py-3" style={{ background: `${ac}08`, borderBottom: `2px solid ${ac}` }}>
          <p className="text-[9px] font-black uppercase tracking-wider mb-1" style={{ color: ac }}>Impact at a Glance</p>
          <p className="text-[11px] leading-relaxed text-gray-700" dangerouslySetInnerHTML={{ __html: resume.summary }} />
        </div>
      )}
      <div className="flex">
        <div className="w-[32%] p-5 space-y-5" style={{ background: '#f8f9fa', borderRight: '1px solid #e5e7eb' }}>
          <div>
            <p className="text-[8px] font-black uppercase tracking-widest mb-2" style={{ color: ac }}>Contact</p>
            {c.email && <div className="flex items-center gap-1.5 mb-1"><Mail className="w-3 h-3 text-gray-400" /><span className="text-[9px] text-gray-600">{c.email}</span></div>}
            {c.phone && <div className="flex items-center gap-1.5 mb-1"><Phone className="w-3 h-3 text-gray-400" /><span className="text-[9px] text-gray-600">{c.phone}</span></div>}
            {c.location && <div className="flex items-center gap-1.5 mb-1"><MapPin className="w-3 h-3 text-gray-400" /><span className="text-[9px] text-gray-600">{c.location}</span></div>}
            {c.linkedin && <div className="flex items-center gap-1.5 mb-1"><Linkedin className="w-3 h-3" style={{ color: ac }} /><span className="text-[9px]" style={{ color: ac }}>{c.linkedin}</span></div>}
            {c.website && <div className="flex items-center gap-1.5"><Globe className="w-3 h-3" style={{ color: ac }} /><span className="text-[9px]" style={{ color: ac }}>{c.website}</span></div>}
          </div>
          {resume.skills?.length > 0 && (
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest mb-2" style={{ color: ac }}>Technical Arsenal</p>
              {resume.skills.map((s, i) => (
                <div key={i} className="flex items-center justify-between mb-1">
                  <span className="text-[9px] text-gray-700">{s.name}</span>
                  <div className="flex gap-px">{[1,2,3,4].map(n => (
                    <div key={n} className="w-1.5 h-1.5 rounded-full"
                      style={{ background: n <= (s.level === 'Expert' ? 4 : s.level === 'Advanced' ? 3 : s.level === 'Intermediate' ? 2 : 1) ? ac : '#e5e7eb' }} />
                  ))}</div>
                </div>
              ))}
            </div>
          )}
          {resume.education?.length > 0 && (
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest mb-2" style={{ color: ac }}>Education</p>
              {resume.education.map((e, i) => (
                <div key={i} className="mb-2">
                  <p className="text-[10px] font-bold text-gray-900">{e.degree}</p>
                  <p className="text-[9px] text-gray-500">{e.school} {e.year && `(${e.year})`}</p>
                </div>
              ))}
            </div>
          )}
          {resume.certifications?.length > 0 && (
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest mb-2" style={{ color: ac }}>Awards & Certs</p>
              {resume.certifications.map((c2, i) => (
                <p key={i} className="text-[9px] text-gray-700 mb-1">{c2.name} {c2.year && `(${c2.year})`}</p>
              ))}
            </div>
          )}
        </div>
        <div className="flex-1 p-5 space-y-4">
          {resume.experience?.length > 0 && (
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest mb-2.5" style={{ color: ac }}>Professional Experience</p>
              {resume.experience.map((e, i) => (
                <div key={i} className="mb-3.5 pb-3.5" style={{ borderBottom: i < resume.experience.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div className="flex justify-between mb-0.5">
                    <span className="text-[11px] font-bold text-gray-900">{e.title}</span>
                    <span className="text-[9px] text-gray-500">{e.start_date}{e.end_date ? ` - ${e.end_date}` : ''}</span>
                  </div>
                  <p className="text-[10px] font-semibold mb-1" style={{ color: ac }}>{e.company} {e.location && `| ${e.location}`}</p>
                  {e.bullets?.filter(Boolean).map((b, bi) => (
                    <p key={bi} className="text-[10px] text-gray-600 ml-2 mb-0.5" dangerouslySetInnerHTML={{ __html: `&bull; ${b}` }} />
                  ))}
                </div>
              ))}
            </div>
          )}
          {resume.projects?.length > 0 && (
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest mb-2" style={{ color: ac }}>Projects</p>
              {resume.projects.map((p, i) => (
                <div key={i} className="mb-2">
                  <span className="text-[10px] font-bold text-gray-900">{p.name}</span>
                  {p.description && <p className="text-[9px] text-gray-600" dangerouslySetInnerHTML={{ __html: p.description }} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModernView({ resume, photo, ac }) {
  const c = resume.contact || {};
  return (
    <div className="bg-white text-gray-900 overflow-hidden shadow-2xl" style={{ fontSize: 12 }}>
      <div className="flex">
        <div className="w-[35%] p-6" style={{ background: '#1a202c', color: '#e2e8f0' }}>
          {photo && <img src={`data:image/jpeg;base64,${photo}`} alt="" className="w-24 h-24 rounded-xl mx-auto mb-4 object-cover" />}
          <h2 className="text-lg font-bold text-white text-center mb-0.5">{c.name || 'Professional'}</h2>
          {c.email && <p className="text-[10px] text-center text-gray-400">{c.email}</p>}
          {c.phone && <p className="text-[10px] text-center text-gray-400">{c.phone}</p>}
          {c.location && <p className="text-[10px] text-center text-gray-400 mb-4">{c.location}</p>}
          {resume.skills?.length > 0 && (
            <div className="mt-4">
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-2">Skills</p>
              {resume.skills.map((s, i) => (
                <div key={i} className="flex justify-between mb-0.5">
                  <span className="text-[10px]">{s.name}</span>
                  <span className="text-[8px] font-bold" style={{ color: ac }}>{s.level}</span>
                </div>
              ))}
            </div>
          )}
          {resume.certifications?.length > 0 && (
            <div className="mt-4">
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-2">Certifications</p>
              {resume.certifications.map((c2, i) => <p key={i} className="text-[10px] mb-0.5">{c2.name}</p>)}
            </div>
          )}
        </div>
        <div className="flex-1 p-6">
          {resume.summary && (
            <div className="mb-4">
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Summary</p>
              <p className="text-[11px] leading-relaxed text-gray-700" dangerouslySetInnerHTML={{ __html: resume.summary }} />
            </div>
          )}
          {resume.experience?.length > 0 && (
            <div className="mb-4">
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-2">Experience</p>
              {resume.experience.map((e, i) => (
                <div key={i} className="mb-3">
                  <div className="flex justify-between">
                    <span className="text-[11px] font-bold text-gray-900">{e.title}</span>
                    <span className="text-[9px] text-gray-500">{e.start_date}{e.end_date ? ` - ${e.end_date}` : ''}</span>
                  </div>
                  <p className="text-[10px] font-semibold" style={{ color: ac }}>{e.company}</p>
                  {e.bullets?.filter(Boolean).map((b, bi) => <p key={bi} className="text-[10px] text-gray-600 ml-2" dangerouslySetInnerHTML={{ __html: `&bull; ${b}` }} />)}
                </div>
              ))}
            </div>
          )}
          {resume.education?.length > 0 && (
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Education</p>
              {resume.education.map((e, i) => (
                <div key={i} className="flex justify-between mb-1.5">
                  <span className="text-[11px] font-bold">{e.degree} <span className="font-normal text-gray-500">- {e.school}</span></span>
                  <span className="text-[9px] text-gray-500">{e.year}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClassicView({ resume, photo, ac }) {
  const c = resume.contact || {};
  return (
    <div className="bg-white text-gray-900 overflow-hidden shadow-2xl p-8" style={{ fontSize: 12 }}>
      <div className="text-center mb-5 pb-4" style={{ borderBottom: `2px solid ${ac}` }}>
        {photo && <img src={`data:image/jpeg;base64,${photo}`} alt="" className="w-20 h-20 rounded-full mx-auto mb-3 object-cover" />}
        <h2 className="text-xl font-bold text-gray-900">{c.name || 'Professional'}</h2>
        <p className="text-[10px] text-gray-500 mt-1">{[c.email, c.phone, c.location].filter(Boolean).join(' | ')}</p>
      </div>
      {resume.summary && (
        <div className="mb-4">
          <p className="text-[9px] font-bold uppercase text-gray-400 mb-1.5">Summary</p>
          <p className="text-[11px] leading-relaxed text-gray-700" dangerouslySetInnerHTML={{ __html: resume.summary }} />
        </div>
      )}
      {resume.experience?.length > 0 && (
        <div className="mb-4">
          <p className="text-[9px] font-bold uppercase text-gray-400 mb-2">Experience</p>
          {resume.experience.map((e, i) => (
            <div key={i} className="mb-3">
              <div className="flex justify-between">
                <span className="text-[11px] font-bold">{e.title} <span className="font-normal" style={{ color: ac }}>at {e.company}</span></span>
                <span className="text-[9px] text-gray-500">{e.start_date}{e.end_date ? ` - ${e.end_date}` : ''}</span>
              </div>
              {e.bullets?.filter(Boolean).map((b, bi) => <p key={bi} className="text-[10px] text-gray-600 ml-3" dangerouslySetInnerHTML={{ __html: `&bull; ${b}` }} />)}
            </div>
          ))}
        </div>
      )}
      {resume.education?.length > 0 && (
        <div className="mb-4">
          <p className="text-[9px] font-bold uppercase text-gray-400 mb-1.5">Education</p>
          {resume.education.map((e, i) => (
            <div key={i} className="flex justify-between mb-1.5">
              <span className="text-[11px] font-bold">{e.degree} <span className="font-normal text-gray-500">- {e.school}</span></span>
              <span className="text-[9px] text-gray-500">{e.year}</span>
            </div>
          ))}
        </div>
      )}
      {resume.skills?.length > 0 && (
        <div>
          <p className="text-[9px] font-bold uppercase text-gray-400 mb-1.5">Skills</p>
          <div className="flex flex-wrap gap-1.5">
            {resume.skills.map((s, i) => (
              <span key={i} className="text-[9px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">{s.name}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicSharedResumePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await resumeAPI.getPublicResume(shareId);
        setData(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Resume not found or link has expired.' : 'Failed to load resume.');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center">
      <div className="text-center">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <h2 className="text-lg font-bold text-gray-700 mb-1">{error}</h2>
        <Link to="/" className="text-sm text-teal-600 hover:underline">Go to Kairera</Link>
      </div>
    </div>
  );

  const resume = data?.resume || {};
  const photo = data?.photo;
  const template = data?.template || 'modern';
  const ac = data?.accent_color || '#0D9488';
  const userName = data?.user_name || 'Professional';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100" data-testid="public-resume-page">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: ac }}>
              <span className="text-white font-black text-xs">K</span>
            </div>
            <span className="font-bold text-gray-800 text-sm">Kairera</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/resume/public/download-pdf/${shareId}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg text-white"
              style={{ background: ac }}
              data-testid="public-resume-download-pdf">
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
            <span className="text-xs text-gray-500">
              {userName}'s Resume
            </span>
            <a href="/" className="text-xs font-bold px-3 py-1.5 rounded-lg" style={{ background: `${ac}10`, color: ac }}>
              Build Yours Free
            </a>
          </div>
        </div>
      </div>

      {/* Resume Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="rounded-2xl overflow-hidden">
            {template === 'impact' && <ImpactView resume={resume} photo={photo} ac={ac} />}
            {template === 'modern' && <ModernView resume={resume} photo={photo} ac={ac} />}
            {(template === 'classic' || template === 'executive') && <ClassicView resume={resume} photo={photo} ac={ac} />}
          </div>
        </motion.div>

        {/* Assessment badges if available */}
        {data?.assessments?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-6 bg-white rounded-2xl shadow-lg p-5"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Career Intelligence Verified</p>
            <div className="flex flex-wrap gap-2">
              {data.assessments.map((a, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-50 border border-teal-100">
                  <Award className="w-3.5 h-3.5 text-teal-600" />
                  <span className="text-xs font-semibold text-teal-700">{a.battery_id?.replace(/_/g, ' ')}</span>
                  {a.scores?.overall !== undefined && (
                    <span className="text-[10px] font-black ml-1 px-1.5 py-0.5 rounded bg-teal-100 text-teal-700">
                      {Math.round(a.scores.overall)}%
                    </span>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer */}
        <div className="text-center mt-10 pb-8">
          <p className="text-xs text-gray-400">
            Powered by <a href="/" className="font-bold" style={{ color: ac }}>Kairera</a> Career Intelligence
          </p>
        </div>
      </div>
    </div>
  );
}
